import { useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStar,
  faChevronLeft,
  faChevronRight,
  faCompass,
  faSearch,
  faSliders,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import SectionHeading from "../Shared/SectionHeading";
import BookCover from "../Shared/BookCover";
import Modal from "./Modal";
import { useT, useI18n } from "../../i18n/I18nContext";
import { categoryGroupFor, findCategoryByValue } from "../CreateProject/categoryCatalog";

// Catalogue showcase — the browse page's vitrine.
//
// Sits above the personal "Your Stories" + "Recommended" grids and
// gives the page a strong identity: a horizontally-scrolling rail of
// poster-style book cards (large covers, gradient overlay, title +
// author + rating badge layered on top) with a category filter pill
// row, plus a search box, sort dropdown, and an expandable
// "Advanced filters" panel for audience / language / minimum-rating
// refinement.
//
// Selecting a card opens the rich ``<Modal>`` (story details, story
// notes, table of contents) instead of navigating straight to /read,
// so the writer can preview before committing to opening the reader.
//
// The component is purely presentational over the public-projects
// payload the parent already fetches for "Recommended" — no extra
// API calls.
// Category filter set — the 4 parent buckets the catalog has
// always exposed (All / Biography / Fiction / Non-Fiction). The
// create form now writes a richer set of sub-categories (Novel,
// Fantasy, Memoir, Self-Help, …) on top of the original three
// values; each sub-category declares its parent group in
// ``categoryCatalog``, so the parent chips here roll up via
// ``categoryGroupFor`` and a Fantasy project still appears under
// the Fiction chip.
//
// Legacy fallbacks (raw substring "non-fiction" / "biography" /
// "fiction" on the project's category string) are kept so rows
// written before the catalogue extension still resolve to a
// parent — the catalogue lookup returns null for unknown free-
// form strings.
//
// Labels reuse `create_project.category.*` i18n keys so the chip
// text is identical to the create form's dropdown.
const _normalise = (p, field) => (p?.[field] || "").toLowerCase();
const _matchesGroup = (p, group) => {
  const raw = p?.category || "";
  const resolved = categoryGroupFor(raw);
  if (resolved === group) return true;
  // Legacy free-form rows: substring match on the parent label.
  const lc = raw.toLowerCase();
  if (group === "biography")   return lc.includes("biography") || lc.includes("biographie");
  if (group === "fiction")     return lc === "fiction";
  if (group === "non_fiction") return lc === "non-fiction" || lc === "non fiction" || lc === "nonfiction";
  return false;
};

const CATEGORIES = [
  { id: "all", labelKey: "browse.catalogue.filter.all", match: () => true },
  {
    id: "biography",
    labelKey: "create_project.category.biography",
    match: (p) => _matchesGroup(p, "biography"),
  },
  {
    id: "fiction",
    labelKey: "create_project.category.fiction",
    match: (p) => _matchesGroup(p, "fiction"),
  },
  {
    id: "non_fiction",
    labelKey: "create_project.category.non_fiction",
    match: (p) => _matchesGroup(p, "non_fiction"),
  },
];

// Audience filter set — same source-of-truth pattern: values
// match `create_project.audience.*` (Children/Teenagers/Adults/
// Elders) so the catalog filter dropdown reads with the same
// labels writers see when they create.
const AUDIENCES = [
  { value: "Children", labelKey: "create_project.audience.children" },
  { value: "Teenagers", labelKey: "create_project.audience.teenagers" },
  { value: "Adults", labelKey: "create_project.audience.adults" },
  { value: "Elders", labelKey: "create_project.audience.elders" },
];

// Sort orders. Each entry exposes a comparator over the raw
// project payload so the sort is data-faithful (no date-string sort
// tricks). ``popular`` is the server's default order so we keep its
// row positions instead of re-sorting locally.
const SORTS = [
  { id: "popular", labelKey: "browse.catalogue.sort.popular", cmp: null },
  {
    id: "newest",
    labelKey: "browse.catalogue.sort.newest",
    cmp: (a, b) =>
      new Date(b.updated_at || b.created_at || 0) -
      new Date(a.updated_at || a.created_at || 0),
  },
  {
    id: "rated",
    labelKey: "browse.catalogue.sort.rated",
    cmp: (a, b) =>
      (Number(b.rating_average) || 0) - (Number(a.rating_average) || 0),
  },
  {
    id: "title",
    labelKey: "browse.catalogue.sort.title",
    cmp: (a, b) => (a.title || "").localeCompare(b.title || ""),
  },
];

const SkeletonRail = () => (
  <div className="flex gap-4 overflow-x-hidden" aria-hidden="true">
    {Array.from({ length: 6 }, (_, i) => (
      <div
        key={i}
        className="flex-shrink-0 w-[180px] sm:w-[200px] aspect-[2/3] rounded-2xl bg-surfaceAlt animate-pulse"
      />
    ))}
  </div>
);

const PosterCard = ({ project, onOpen }) => {
  const t = useT();
  const rating = Number(project.rating_average || 0);
  const showRating = rating > 0;
  return (
    <button
      type="button"
      onClick={() => onOpen(project)}
      className="group relative flex-shrink-0 w-[180px] sm:w-[200px] aspect-[2/3] rounded-2xl overflow-hidden border border-border bg-surfaceAlt shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all snap-start text-left focus:outline-none focus:ring-2 focus:ring-primary/40"
      aria-label={project.title}
    >
      <BookCover
        src={project.cover_url || ""}
        alt={project.title || ""}
        className="absolute inset-0 w-full h-full object-cover"
        iconClassName="text-secondText text-3xl"
      />
      <div
        className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent pointer-events-none"
        aria-hidden="true"
      />
      {showRating ? (
        <span className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-full bg-black/60 backdrop-blur-sm text-white px-2 py-0.5 text-[11px] font-semibold">
          <FontAwesomeIcon icon={faStar} className="text-warning text-[10px]" />
          {rating.toFixed(1)}
        </span>
      ) : null}
      <div className="absolute inset-x-0 bottom-0 p-3 text-white">
        <p className="text-sm font-semibold leading-tight line-clamp-2 mb-0.5">
          {project.title || t("browse.catalogue.untitled")}
        </p>
        {project.author ? (
          <p className="text-[11px] text-white/80 truncate">
            {t("book.by_author", { author: project.author })}
          </p>
        ) : null}
      </div>
    </button>
  );
};

const CatalogueShowcase = ({ projects, loading, excludeIds }) => {
  const t = useT();
  const { locale } = useI18n();
  const currentLocale = locale === "fr" ? "fr" : "en";
  const [filterId, setFilterId] = useState("all");
  const [sortId, setSortId] = useState("popular");
  const [search, setSearch] = useState("");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [audience, setAudience] = useState("");
  const [minRating, setMinRating] = useState(0);
  // Locale filter — default = current UI locale so a FR reader
  // doesn't land on a wall of EN books they can't read. Switch to
  // ``all`` to opt back into mixed display, or the other locale
  // to flip the focus.
  const [localeFilter, setLocaleFilter] = useState(currentLocale);
  const [openProject, setOpenProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const railRef = useRef(null);

  // Available audience values restricted to the canonical set the
  // create form writes. Earlier code drew the dropdown options from
  // distinct values in the live pool, which surfaced raw strings
  // (`"Adults"`) untranslated and could include typo'd / legacy
  // values. The canonical AUDIENCES list keeps the filter coherent
  // with the writer-side dropdown labels.
  const availableAudiences = useMemo(() => {
    const present = new Set(
      (projects || []).map((p) => p.target_audience).filter(Boolean),
    );
    return AUDIENCES.filter((a) => present.has(a.value));
  }, [projects]);

  // Locale counts driving the chip badges. Computed before any
  // category / search filter so the badges show the total split
  // available in the pool — switching the locale filter doesn't
  // change the badge values, only what passes through.
  const localeCounts = useMemo(() => {
    const counts = { fr: 0, en: 0, all: 0 };
    (projects || []).forEach((p) => {
      const lang = (p.language || "en").toLowerCase().slice(0, 2);
      if (lang === "fr") counts.fr += 1;
      else counts.en += 1;
      counts.all += 1;
    });
    return counts;
  }, [projects]);

  // Dynamic chip strip: the four hard-coded parent chips (All /
  // Biography / Fiction / Non-Fiction) PLUS one chip per sub-
  // category that has at least one project in the current pool.
  // Without the sub-chips the writer could see that something is
  // tagged "Drama" or "Short Story" inside the cards but had no
  // single-click filter for it — too coarse for a 20+ catalog.
  //
  // Sub-chips are sorted by popularity (the catalogue's hand-picked
  // score) so Novel surfaces before Drama, Drama before Short Story,
  // etc. Filter id for a sub-chip is the canonical catalogue value
  // verbatim (``"Novel"``, ``"Drama"``) so a click maps cleanly to
  // ``p.category === id``.
  const categoryChips = useMemo(() => {
    const fixed = CATEGORIES; // parent buckets (All / Bio / Fiction / NF)
    if (!projects?.length) return fixed;
    const seen = new Map();    // canonical value → { entry, count }
    for (const p of projects) {
      const raw = (p?.category || "").trim();
      if (!raw) continue;
      const entry = findCategoryByValue(raw);
      if (!entry || entry.group === undefined) continue;
      // Skip the parent buckets themselves — they're already in
      // ``fixed``. Sub-categories are catalogue entries whose
      // value differs from their group label.
      if (
        entry.value.toLowerCase() === "fiction" ||
        entry.value.toLowerCase() === "non-fiction" ||
        entry.value.toLowerCase() === "biography"
      ) continue;
      const key = entry.value;
      const slot = seen.get(key) || { entry, count: 0 };
      slot.count += 1;
      seen.set(key, slot);
    }
    const subChips = Array.from(seen.values())
      .sort((a, b) => (b.entry.popularity || 0) - (a.entry.popularity || 0))
      .map(({ entry }) => ({
        id: entry.value,
        labelKey: entry.labelKey,
        match: (p) =>
          (p?.category || "").toLowerCase() === entry.value.toLowerCase(),
      }));
    return [...fixed, ...subChips];
  }, [projects]);

  const visible = useMemo(() => {
    const filter = categoryChips.find((c) => c.id === filterId) ?? categoryChips[0];
    const q = search.trim().toLowerCase();
    let list = (projects || []).filter(filter.match);
    // De-dup against the surfaces above (Your stories + Recommended)
    // so a project that already showed in one of those rails doesn't
    // appear AGAIN in the catalogue rail right below — fixes the
    // "Liaisons dangereuses appears twice on /browse" report.
    if (excludeIds && excludeIds.size > 0) {
      list = list.filter((p) => !excludeIds.has(String(p.id)));
    }
    // Apply locale filter early — it's a coarse cut that most
    // dramatically shrinks the pool, so subsequent filters work
    // on the relevant subset.
    if (localeFilter !== "all") {
      list = list.filter((p) => {
        const lang = (p.language || "en").toLowerCase().slice(0, 2);
        return lang === localeFilter;
      });
    }
    if (q) {
      list = list.filter((p) => {
        const haystack = `${p.title || ""} ${p.description || ""} ${
          (p.tags || []).join(" ") || ""
        } ${p.author || ""}`.toLowerCase();
        return haystack.includes(q);
      });
    }
    if (audience) list = list.filter((p) => p.target_audience === audience);
    if (minRating > 0)
      list = list.filter(
        (p) => (Number(p.rating_average) || 0) >= minRating,
      );
    const sort = SORTS.find((s) => s.id === sortId);
    if (sort?.cmp) list = [...list].sort(sort.cmp);
    return list;
  }, [projects, categoryChips, excludeIds, filterId, localeFilter, search, audience, minRating, sortId]);

  const scrollBy = (direction) => {
    const el = railRef.current;
    if (!el) return;
    el.scrollBy({
      left: direction * Math.round(el.clientWidth * 0.8),
      behavior: "smooth",
    });
  };

  const handleOpen = (project) => {
    setOpenProject(project);
    setIsModalOpen(true);
  };

  // ``advancedActive`` drives the highlighted-pill state on the
  // Advanced toggle so the writer can see at a glance that the
  // panel is hiding a non-default filter. Language is part of the
  // panel now, so it counts: ``all`` means the writer expanded the
  // catalog beyond their UI locale → highlight.
  const advancedActive =
    !!audience ||
    minRating > 0 ||
    sortId !== "popular" ||
    (localeFilter && localeFilter !== currentLocale);

  return (
    <section className="mb-8 md:mb-12">
      <div className="mb-4 md:mb-6 flex flex-wrap justify-between items-end gap-3">
        <SectionHeading
          heading={t("browse.catalogue.title")}
          text={t("browse.catalogue.subtitle")}
        />
        <div className="hidden md:flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={() => scrollBy(-1)}
            aria-label={t("browse.catalogue.prev")}
            className="inline-flex w-9 h-9 items-center justify-center rounded-full text-text border border-border bg-surface hover:bg-surfaceAlt hover:text-primary transition"
          >
            <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
          </button>
          <button
            type="button"
            onClick={() => scrollBy(1)}
            aria-label={t("browse.catalogue.next")}
            className="inline-flex w-9 h-9 items-center justify-center rounded-full text-text border border-border bg-surface hover:bg-surfaceAlt hover:text-primary transition"
          >
            <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
          </button>
        </div>
      </div>

      {/* Search + sort + advanced toggle. Single row on wide
          viewports; wraps gracefully on narrow ones. */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[14rem]">
          <FontAwesomeIcon
            icon={faSearch}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-secondText text-xs"
            aria-hidden="true"
          />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("browse.catalogue.search.placeholder")}
            aria-label={t("browse.catalogue.search.placeholder")}
            className="w-full bg-surface border border-border rounded-full pl-8 pr-3 py-2 text-sm text-text placeholder:text-secondText focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 transition"
          />
          {search ? (
            <button
              type="button"
              onClick={() => setSearch("")}
              aria-label={t("browse.catalogue.search.clear")}
              className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex w-6 h-6 items-center justify-center rounded-full text-secondText hover:bg-surfaceAlt hover:text-text transition"
            >
              <FontAwesomeIcon icon={faTimes} className="text-[10px]" />
            </button>
          ) : null}
        </div>
        <select
          value={sortId}
          onChange={(e) => setSortId(e.target.value)}
          aria-label={t("browse.catalogue.sort.label")}
          className="bg-surface border border-border rounded-full px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 transition"
        >
          {SORTS.map((s) => (
            <option key={s.id} value={s.id}>
              {t(s.labelKey)}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => setAdvancedOpen((v) => !v)}
          className={`inline-flex items-center gap-2 text-sm font-semibold px-3 py-2 rounded-full border transition ${
            advancedOpen || advancedActive
              ? "bg-primaryMuted text-primary border-primary/40"
              : "bg-surface text-text border-border hover:bg-surfaceAlt"
          }`}
          aria-expanded={advancedOpen}
        >
          <FontAwesomeIcon icon={faSliders} className="text-xs" />
          {t("browse.catalogue.advanced")}
        </button>
      </div>

      {/* Advanced filter panel — collapsed by default. Language
          lives here now (used to ride alongside Search / Sort in
          the toolbar row) so the toolbar stays focused on the
          two controls writers actually touch every visit. Reset
          button clears every advanced filter at once. */}
      {advancedOpen ? (
        <div className="mb-3 rounded-xl border border-border bg-surfaceAlt p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="flex flex-col gap-1 text-xs text-secondText">
            {t("browse.catalogue.advanced.language")}
            <select
              value={localeFilter}
              onChange={(e) => setLocaleFilter(e.target.value)}
              className="bg-surface border border-border rounded-lg px-3 py-1.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              {/* Current UI locale first — it's the default the
                  filter starts at, so users who never touch this
                  see their own language at the top of the list.
                  "Other locale" then "all" so the dropdown reads:
                  same → different → no filter. */}
              <option value={currentLocale}>
                {t(`locale_filter.${currentLocale}`)}
                {typeof localeCounts?.[currentLocale] === "number"
                  ? ` · ${localeCounts[currentLocale]}`
                  : ""}
              </option>
              <option value={currentLocale === "fr" ? "en" : "fr"}>
                {t(`locale_filter.${currentLocale === "fr" ? "en" : "fr"}`)}
                {typeof localeCounts?.[currentLocale === "fr" ? "en" : "fr"] === "number"
                  ? ` · ${localeCounts[currentLocale === "fr" ? "en" : "fr"]}`
                  : ""}
              </option>
              <option value="all">
                {t("locale_filter.all")}
                {typeof localeCounts?.all === "number"
                  ? ` · ${localeCounts.all}`
                  : ""}
              </option>
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-secondText">
            {t("browse.catalogue.advanced.audience")}
            <select
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              className="bg-surface border border-border rounded-lg px-3 py-1.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value="">{t("browse.catalogue.advanced.any")}</option>
              {availableAudiences.map((a) => (
                <option key={a.value} value={a.value}>
                  {t(a.labelKey)}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-secondText">
            {t("browse.catalogue.advanced.min_rating")}
            <select
              value={minRating}
              onChange={(e) => setMinRating(Number(e.target.value))}
              className="bg-surface border border-border rounded-lg px-3 py-1.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value={0}>{t("browse.catalogue.advanced.any")}</option>
              <option value={3}>3+ ★</option>
              <option value={4}>4+ ★</option>
              <option value={4.5}>4.5+ ★</option>
            </select>
          </label>
          {advancedActive ? (
            <div className="sm:col-span-2 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setAudience("");
                  setMinRating(0);
                  setSortId("popular");
                  // Reset language to the writer's UI locale —
                  // that's the "default" the filter starts at,
                  // not "all", so clicking Reset restores the
                  // initial state, not a wider one.
                  setLocaleFilter(currentLocale);
                }}
                className="text-xs text-secondText hover:text-text underline"
              >
                {t("browse.catalogue.advanced.reset")}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Category filter pills with per-bucket counts. Includes
          the parent buckets (Biography / Fiction / Non-Fiction)
          PLUS any sub-category — Novel, Drama, Short Story, etc.
          — that has at least one project in the current pool. */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {categoryChips.map((c) => {
          const active = c.id === filterId;
          // Chip counts reflect the post-dedup pool so the badge
          // matches what the rail actually shows — otherwise a chip
          // promising "Fiction (12)" would scroll only 10 cards once
          // 2 of them were hidden as already-on-shelf duplicates.
          const pool = excludeIds && excludeIds.size > 0
            ? (projects || []).filter((p) => !excludeIds.has(String(p.id)))
            : (projects || []);
          const count =
            c.id === "all" ? pool.length : pool.filter(c.match).length;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => setFilterId(c.id)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full transition border ${
                active
                  ? "bg-primaryMuted text-primary border-primary/40"
                  : "bg-surface text-text border-border hover:bg-surfaceAlt"
              }`}
            >
              {t(c.labelKey)}
              <span
                className={`ml-2 inline-flex items-center justify-center rounded-full px-1.5 text-[10px] font-bold ${
                  active ? "bg-primary text-white" : "bg-surfaceAlt text-secondText"
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <SkeletonRail />
      ) : visible.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-surfaceAlt px-6 py-10">
          <div className="flex flex-col items-center text-center max-w-sm mx-auto">
            <div className="flex items-center justify-center w-14 h-14 rounded-full bg-surface border border-border mb-3 text-primary">
              <FontAwesomeIcon icon={faCompass} className="text-xl" />
            </div>
            <h3 className="text-sm font-semibold text-text mb-1">
              {t("browse.catalogue.empty.title")}
            </h3>
            <p className="text-xs text-secondText">
              {t("browse.catalogue.empty.description")}
            </p>
          </div>
        </div>
      ) : (
        <div
          ref={railRef}
          className="flex gap-4 overflow-x-auto custom-scrollbar pb-3 snap-x snap-mandatory scroll-pl-1 -mx-1 px-1"
        >
          {visible.map((p) => (
            <PosterCard key={p.id} project={p} onOpen={handleOpen} />
          ))}
        </div>
      )}

      {/* Project preview modal — ``<Modal>`` lazily fetches the
          full project (story_notes, story_details, chapters) when
          ``openModal`` flips true, so the rich preview is one
          round trip away from any catalogue card. */}
      <Modal
        openModal={isModalOpen}
        setOpenModal={setIsModalOpen}
        projectId={openProject?.id}
        title={openProject?.title}
        img={openProject?.cover_url || ""}
        description={openProject?.description || ""}
        totalParts={
          openProject?.published_chapter_count ?? openProject?.chapter_count ?? 0
        }
        status={
          openProject?.archived
            ? t("home.status.complete")
            : t("home.status.in_progress")
        }
      />
    </section>
  );
};

export default CatalogueShowcase;
